import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "sticky-header-form" }
const _hoisted_3 = { class: "filter-bar" }
const _hoisted_4 = { class: "filter-field" }
const _hoisted_5 = { class: "filter-field" }
const _hoisted_6 = { class: "filter-field" }
const _hoisted_7 = { class: "filter-field" }
const _hoisted_8 = { class: "filter-field" }
const _hoisted_9 = { class: "filter-field" }
const _hoisted_10 = { class: "filter-field" }
const _hoisted_11 = { class: "action-bar" }
const _hoisted_12 = {
  key: 0,
  class: "d-flex justify-content-center my-3"
}
const _hoisted_13 = {
  key: 1,
  class: "events-table"
}
const _hoisted_14 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_15 = ["onDblclick"]
const _hoisted_16 = {
  class: "col-datetime",
  "data-label": "Date/Time"
}
const _hoisted_17 = {
  class: "col-type",
  "data-label": "Type"
}
const _hoisted_18 = ["title"]
const _hoisted_19 = {
  class: "col-client",
  "data-label": "Client"
}
const _hoisted_20 = {
  class: "col-site",
  "data-label": "Site"
}
const _hoisted_21 = {
  class: "col-officer",
  "data-label": "Officer"
}
const _hoisted_22 = {
  class: "col-actions",
  "data-label": "Actions"
}
const _hoisted_23 = {
  class: "col-attachments",
  "data-label": "Files"
}
const _hoisted_24 = ["onClick"]
const _hoisted_25 = {
  key: 2,
  class: "pagination-bar"
}
const _hoisted_26 = { class: "pagination-buttons" }
const _hoisted_27 = ["disabled"]
const _hoisted_28 = ["disabled"]

import { ref, computed, watch, onMounted } from 'vue'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'
import EventFormModal from '../components/EventFormModal.vue'
import EventTypesModal from '../components/EventTypesModal.vue'
import LookupDataService from '../services/LookupDataService'

const pageSize = 20

export default /*@__PURE__*/_defineComponent({
  __name: 'EventsView',
  props: {
    baseUrl: {}
  },
  setup(__props: any) {

const props = __props

const eventTypes = ref<any[]>([])
const sites = ref<any[]>([])
const clientSites = ref<any[]>([])
const officers = ref<any[]>([])
const companies = ref<any[]>([])

const events = ref<any[]>([])
const totalCount = ref(0)
const page = ref(1)
const isLoading = ref(false)

const officerKind = ref<'all' | 'employee' | 'subcontractor'>('all')

const filters = ref<{
  clientId: number | null
  siteId: number | null
  eventTypeId: number | null
  securityOfficerId: number | null
  dateFrom: string
  dateTo: string
}>({
  clientId: null,
  siteId: null,
  eventTypeId: null,
  securityOfficerId: null,
  dateFrom: '',
  dateTo: ''
})

const formModal = ref<InstanceType<typeof EventFormModal> | null>(null)
const typesModal = ref<InstanceType<typeof EventTypesModal> | null>(null)

const rangeStart = computed(() => (totalCount.value === 0 ? 0 : (page.value - 1) * pageSize + 1))
const rangeEnd = computed(() => Math.min(page.value * pageSize, totalCount.value))

const filteredOfficers = computed(() => {
  if (officerKind.value === 'all') return officers.value
  const contractorCompanyIds = new Set(companies.value.filter(c => c.isContractor).map(c => c.id))
  const wantSubcontractor = officerKind.value === 'subcontractor'
  return officers.value.filter(o => {
    const isSubcontractorOfficer = o.securityCompanyId != null && contractorCompanyIds.has(o.securityCompanyId)
    return wantSubcontractor ? isSubcontractorOfficer : !isSubcontractorOfficer
  })
})

const filteredSites = computed(() => (filters.value.clientId ? clientSites.value : sites.value))

async function fetchClientSites(clientId: number | null) {
  if (!clientId) {
    clientSites.value = []
    return
  }
  try {
    const res = await fetch(`${props.baseUrl}ClientSite/ClientSitesByPost`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ Id: clientId, SearchString: '', PageNumber: 0, EntitiesPerPage: 1000 })
    })
    if (res.ok) {
      const result = await res.json()
      clientSites.value = (result.clientSites || []).map((cs: any) => ({ id: cs.siteId, name: cs.siteInfo }))
      if (!clientSites.value.some((s: any) => s.id === filters.value.siteId)) {
        filters.value.siteId = null
      }
    }
  } catch (e) {
    console.error('Failed to load sites for client:', e)
  }
}

watch(() => filters.value.clientId, (clientId) => {
  fetchClientSites(clientId)
})

function officerDisplayLabel(o: any): string {
  if (officerKind.value !== 'subcontractor') return o.name
  const company = companies.value.find(c => c.id === o.securityCompanyId)
  return company ? `${company.name}: ${o.name}` : o.name
}

const typeBadgeClasses: Record<string, string> = {
  complaint: 'badge-complaint',
  incident: 'badge-incident',
  accident: 'badge-accident',
  lateness: 'badge-lateness',
  'blow out': 'badge-blowout',
}

function typeBadgeClass(name: string): string {
  return typeBadgeClasses[(name || '').toLowerCase()] || 'badge-other'
}

function formatDateTime(value: string): string {
  if (!value) return 'N/A'
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  } catch {
    return value
  }
}

function truncate(text: string): string {
  if (!text) return '-'
  return text.length > 80 ? text.slice(0, 80) + '…' : text
}

function loadLookupData() {
  const data = LookupDataService.getData()
  if (data) {
    eventTypes.value = data.eventTypes || []
    sites.value = data.sites || []
    officers.value = data.officers || []
    companies.value = data.companies || []
  }
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.token}`, 'Content-Type': 'application/json' }
}

async function search(targetPage: number) {
  page.value = targetPage;
  isLoading.value = true
  try {
    const res = await fetch(props.baseUrl + 'Events/Search', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        ClientId: filters.value.clientId,
        SiteId: filters.value.siteId,
        EventTypeId: filters.value.eventTypeId,
        SecurityOfficerId: filters.value.securityOfficerId,
        IsSubcontractor: officerKind.value === 'all' ? null : officerKind.value === 'subcontractor',
        DateFrom: filters.value.dateFrom || null,
        DateTo: filters.value.dateTo || null,
        Page: page.value,
        PageSize: pageSize
      })
    })
    if (!res.ok) {
      const body = await res.text()
      console.error(`Events/Search failed (${res.status}):`, body)
      return
    }
    const result = await res.json()
    if (result.result) {
      events.value = result.result.events || []
      totalCount.value = result.result.totalCount || 0
    }
  } catch (e) {
    console.error('Failed to search events:', e)
  } finally {
    isLoading.value = false
  }
}

function clearFilters() {
  filters.value = { clientId: null, siteId: null, eventTypeId: null, securityOfficerId: null, dateFrom: '', dateTo: '' }
  officerKind.value = 'all'
  search(1)
}

function openAddEvent() {
  formModal.value?.openForAdd()
}

function openEditEvent(event: any) {
  formModal.value?.openForEdit(event)
}

function openViewEvent(event: any) {
  formModal.value?.openForView(event)
}

function openManageTypes() {
  typesModal.value?.openModal()
}

function onModalClosed() {
  search(page.value)
}

function onEventSaved() {
  search(page.value)
}

function onTypesModalClosed() {
  loadLookupData()
}

onMounted(() => {
  loadLookupData()
  search(1)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[19] || (_cache[19] = _createElementVNode("h3", { class: "text-center" }, "EVENTS", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[11] || (_cache[11] = _createElementVNode("label", null, "Client", -1)),
          _createVNode(_unref(Multiselect), {
            modelValue: filters.value.clientId,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.clientId) = $event)),
            options: companies.value.map(c => ({ value: c.id, label: c.name })),
            placeholder: "All clients",
            searchable: true,
            clearable: true
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _cache[12] || (_cache[12] = _createElementVNode("label", null, "Site", -1)),
          _createVNode(_unref(Multiselect), {
            modelValue: filters.value.siteId,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.siteId) = $event)),
            options: filteredSites.value.map(s => ({ value: s.id, label: s.name })),
            placeholder: "All sites",
            searchable: true,
            clearable: true
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[13] || (_cache[13] = _createElementVNode("label", null, "Event Type", -1)),
          _createVNode(_unref(Multiselect), {
            modelValue: filters.value.eventTypeId,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.eventTypeId) = $event)),
            options: eventTypes.value.map(t => ({ value: t.id, label: t.name })),
            placeholder: "All types",
            searchable: true,
            clearable: true
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _cache[15] || (_cache[15] = _createElementVNode("label", null, "Officer Kind", -1)),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((officerKind).value = $event))
          }, [...(_cache[14] || (_cache[14] = [
            _createElementVNode("option", { value: "all" }, "All", -1),
            _createElementVNode("option", { value: "employee" }, "Employee", -1),
            _createElementVNode("option", { value: "subcontractor" }, "Subcontractor", -1)
          ]))], 512), [
            [_vModelSelect, officerKind.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _cache[16] || (_cache[16] = _createElementVNode("label", null, "Officer", -1)),
          _createVNode(_unref(Multiselect), {
            modelValue: filters.value.securityOfficerId,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.securityOfficerId) = $event)),
            options: filteredOfficers.value.map(o => ({ value: o.id, label: officerDisplayLabel(o) })),
            placeholder: "All officers",
            searchable: true,
            clearable: true
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _cache[17] || (_cache[17] = _createElementVNode("label", null, "From", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "date",
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.value.dateFrom) = $event))
          }, null, 512), [
            [_vModelText, filters.value.dateFrom]
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _cache[18] || (_cache[18] = _createElementVNode("label", null, "To", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "date",
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.value.dateTo) = $event))
          }, null, 512), [
            [_vModelText, filters.value.dateTo]
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("button", {
          class: "btn btn-primary btn-sm",
          onClick: _cache[7] || (_cache[7] = ($event: any) => (search(1)))
        }, "Search"),
        _createElementVNode("button", {
          class: "btn btn-secondary btn-sm",
          onClick: clearFilters
        }, "Clear"),
        _createElementVNode("button", {
          class: "btn btn-outline-secondary btn-sm",
          onClick: openManageTypes
        }, "Manage Event Types"),
        _createElementVNode("button", {
          class: "btn btn-success btn-sm ms-auto",
          onClick: openAddEvent
        }, "Add Event")
      ])
    ]),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [...(_cache[20] || (_cache[20] = [
          _createElementVNode("div", { class: "spinner-border text-primary" }, [
            _createElementVNode("span", { class: "visually-hidden" })
          ], -1)
        ]))]))
      : (_openBlock(), _createElementBlock("div", _hoisted_13, [
          _cache[21] || (_cache[21] = _createStaticVNode("<div class=\"table-header\" data-v-785e6ce5><div class=\"col-datetime\" data-v-785e6ce5>Date/Time</div><div class=\"col-type\" data-v-785e6ce5>Type</div><div class=\"col-detail\" data-v-785e6ce5>Detail</div><div class=\"col-client\" data-v-785e6ce5>Client</div><div class=\"col-site\" data-v-785e6ce5>Site</div><div class=\"col-officer\" data-v-785e6ce5>Officer</div><div class=\"col-actions\" data-v-785e6ce5>Actions</div><div class=\"col-attachments\" data-v-785e6ce5>Files</div><div class=\"col-edit\" data-v-785e6ce5>Edit</div></div>", 1)),
          (events.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, "No events found"))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(events.value, (event) => {
            return (_openBlock(), _createElementBlock("div", {
              key: event.id,
              class: "table-row",
              onDblclick: ($event: any) => (openViewEvent(event))
            }, [
              _createElementVNode("div", _hoisted_16, _toDisplayString(formatDateTime(event.eventDateTime)), 1),
              _createElementVNode("div", _hoisted_17, [
                _createElementVNode("span", {
                  class: _normalizeClass(["badge", typeBadgeClass(event.eventTypeName)])
                }, _toDisplayString(event.eventTypeName), 3)
              ]),
              _createElementVNode("div", {
                class: "col-detail",
                "data-label": "Detail",
                title: event.eventDetail
              }, _toDisplayString(truncate(event.eventDetail)), 9, _hoisted_18),
              _createElementVNode("div", _hoisted_19, _toDisplayString(event.clientName || '-'), 1),
              _createElementVNode("div", _hoisted_20, _toDisplayString(event.siteName || '-'), 1),
              _createElementVNode("div", _hoisted_21, _toDisplayString(event.securityOfficerName || '-'), 1),
              _createElementVNode("div", _hoisted_22, _toDisplayString((event.eventActions || []).length), 1),
              _createElementVNode("div", _hoisted_23, _toDisplayString((event.eventAttachments || []).length), 1),
              _createElementVNode("div", {
                class: "col-edit",
                "data-label": "Edit",
                onDblclick: _cache[8] || (_cache[8] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createElementVNode("button", {
                  class: "text-blue-600",
                  onClick: ($event: any) => (openEditEvent(event))
                }, "Edit", 8, _hoisted_24)
              ], 32)
            ], 40, _hoisted_15))
          }), 128))
        ])),
    (totalCount.value > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
          _createElementVNode("span", null, "Showing " + _toDisplayString(rangeStart.value) + "-" + _toDisplayString(rangeEnd.value) + " of " + _toDisplayString(totalCount.value), 1),
          _createElementVNode("div", _hoisted_26, [
            _createElementVNode("button", {
              class: "btn btn-sm btn-outline-secondary",
              disabled: page.value <= 1,
              onClick: _cache[9] || (_cache[9] = ($event: any) => (search(page.value - 1)))
            }, "Prev", 8, _hoisted_27),
            _createElementVNode("button", {
              class: "btn btn-sm btn-outline-secondary",
              disabled: rangeEnd.value >= totalCount.value,
              onClick: _cache[10] || (_cache[10] = ($event: any) => (search(page.value + 1)))
            }, "Next", 8, _hoisted_28)
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(EventFormModal, {
      ref_key: "formModal",
      ref: formModal,
      "base-url": __props.baseUrl,
      onClose: onModalClosed,
      onSaved: onEventSaved
    }, null, 8, ["base-url"]),
    _createVNode(EventTypesModal, {
      ref_key: "typesModal",
      ref: typesModal,
      "base-url": __props.baseUrl,
      onClose: onTypesModalClosed,
      onChanged: loadLookupData
    }, null, 8, ["base-url"])
  ]))
}
}

})